import React, { useState } from 'react';
import Markdown from 'markdown-to-jsx';
import { UserAction } from '@enview/interface/types/actions/UserAction';
import { transformPlainToCEX, postDiffCleanup } from '../../../helpers/markdown';
import { diffToHtml, markdownDiff } from '../../../helpers/dubdiff/util/dubdiff';
import _BillAnnotationFromSelection from '../../../containers/BillAnnotationFromSelection';
import BillAnnotation from '../../../components/BillAnnotation';

// Inject types to satisfy the compiler for a Javascript-defined component
const BillAnnotationFromSelection =
  _BillAnnotationFromSelection as unknown as React.JSXElementConstructor<{
    billId: string;
    billState: string;
    billVersionId: number;
    diffTags: Record<string, unknown>;
    sourceBillText: string;
  }>;

type BillTextMarkdownProps = {
  billId: string;
  billVersionId: number;
  billState: string;
  baseText: string;
  text: string;
  compareToBase: boolean;
  annotations: UserAction[];
  debug: boolean;
};

type DecoratedAnnotation = {
  insertStart: string;
  insertEnd: string;
  fromCharacter: number;
  toCharacter: number;
};

const DIFF_TAGS = {
  added: {
    start: '===',
    end: '===',
  },
  removed: {
    start: '~~',
    end: '~~',
  },
  same: {
    start: '',
    end: '',
  },
};

/**
 * Bill text might include special characters that we do not want to be
 * interpreted as markdown. So we take the protoMarkdown string (bill text)
 * and escape as necessary to avoid errors or unexpected formatting
 * @param protoMarkdown
 * @return {*}
 */
const sanitizeInputMarkdown = (protoMarkdown: string) => {
  return protoMarkdown.replace(/\[/g, '\\[').replace(/\]/g, '\\]');
};

const BillTextMarkdown = (props: BillTextMarkdownProps) => {
  const {
    billId,
    billVersionId,
    billState,
    text,
    baseText,
    compareToBase,
    annotations,
    debug,
  } = props;
  const [viewSourceMarkdown, setViewSourceMarkdown] = useState(false);

  const renderAnnotationStartString = (annotation: UserAction) => {
    const comment = !annotation.comment ? '' : annotation.comment;
    return `<BillAnnotation data-annotationNotes="${comment}" data-annotationId="${annotation.id}">`;
  };

  const decorateAnnotationsWithInsertionStrings = (actions: UserAction[]) => {
    return actions.map((action: UserAction) => {
      const annotation = action.annotation!;
      return {
        fromCharacter: annotation.fromCharacter,
        toCharacter: annotation.toCharacter,
        insertStart: renderAnnotationStartString(action),
        insertEnd: '</BillAnnotation>',
      };
    });
  };

  const calculateInsertionPoints = (
    annotation: DecoratedAnnotation,
    decoratedAnnotations: DecoratedAnnotation[],
  ) => {
    let startDelta = 0;
    let endDelta = 0;

    decoratedAnnotations.forEach((anno) => {
      if (
        anno.fromCharacter < annotation.fromCharacter &&
        anno.toCharacter < annotation.fromCharacter
      ) {
        startDelta += anno.insertStart.length + anno.insertEnd.length;
      } else if (anno.fromCharacter < annotation.fromCharacter) {
        startDelta += anno.insertStart.length;
      }

      if (
        anno.fromCharacter < annotation.toCharacter &&
        anno.toCharacter < annotation.toCharacter
      ) {
        endDelta += anno.insertStart.length + anno.insertEnd.length;
      } else if (anno.fromCharacter < annotation.toCharacter) {
        endDelta += anno.insertStart.length;
      }
    });

    return {
      start: annotation.fromCharacter + startDelta,
      end: annotation.toCharacter + endDelta,
    };
  };

  const insertAnnotations = (initialMarkdown: string) => {
    let markdown = initialMarkdown;
    const decoratedAnnotations = decorateAnnotationsWithInsertionStrings(annotations);
    decoratedAnnotations
      .sort((a, b) => a.fromCharacter - b.fromCharacter)
      .forEach((anno) => {
        const insertPoint = calculateInsertionPoints(anno, decoratedAnnotations);
        markdown = [
          markdown.slice(0, insertPoint.start),
          anno.insertStart,
          markdown.slice(insertPoint.start),
        ].join('');
        markdown = [
          markdown.slice(0, insertPoint.end),
          anno.insertEnd,
          markdown.slice(insertPoint.end),
        ].join('');
      });

    // OK annotations are inserted, now let's make sure HTML tags are proper inside of them
    // Luckily we should only be dealing with ===/~~
    // @TODO deal with spanning block elements!
    const annoRegex = /(<BillAnnotation[^>]+?>)([\s\S]+?)(<\/BillAnnotation>)/gi;
    markdown = markdown.replace(annoRegex, (match, annoOpen, contents, annoClose) => {
      let prefixCloseTag = '';
      let suffixOpenTag = '';
      let newContents = contents;

      // Fix un-opened tag at start
      const firstTagIsClosedRegex = /^(?:[^<]*)(<\s*\/[^>]+?>)/i;
      const firstClosedMatch = firstTagIsClosedRegex.exec(contents);
      if (firstClosedMatch) {
        [prefixCloseTag] = firstClosedMatch;
        newContents = [firstClosedMatch[1].replace('/', ''), newContents].join('');
      }

      // Fix un-closed tags via window.document...innnerHTML
      const div = window.document.createElement('div');
      // nosemgrep
      div.innerHTML = newContents;
      newContents = div.innerHTML;

      const endCloseTagRegex = /<\/[^>]+?>$/i;
      const newContentsEndTagMatches = newContents.match(endCloseTagRegex);
      const contentEndTagMatches = contents.match(endCloseTagRegex);
      if (
        newContentsEndTagMatches &&
        (!contentEndTagMatches ||
          contentEndTagMatches[0] !== newContentsEndTagMatches[0])
      ) {
        // we auto-closed a tag within the BillAnnotation contents, so it needs to be re-inserted after the BA
        suffixOpenTag = newContentsEndTagMatches[0].replace('/', '');
      }

      // Do inserts for any parts of the annotation that spans across block elements
      const blockBoundaryRegex = /\n\n[#*1-9.> ]*/g;
      newContents = newContents.replace(
        blockBoundaryRegex,
        `${annoClose}$&${annoOpen}`,
      );
      return [prefixCloseTag, annoOpen, newContents, annoClose, suffixOpenTag].join('');
    });

    return markdown;
  };

  let displayMarkdown = '';
  let sourceMarkdown = '';
  const noBillVersion =
    '<div>This version is not currently ' +
    'available. Try choosing another version of this bill from the dropdown above.</div>';

  if (compareToBase && baseText) {
    const diff = markdownDiff(
      sanitizeInputMarkdown(baseText),
      sanitizeInputMarkdown(text || ''),
    );
    sourceMarkdown = postDiffCleanup(diffToHtml(diff, DIFF_TAGS));
    displayMarkdown = insertAnnotations(sourceMarkdown);
  } else {
    sourceMarkdown = sanitizeInputMarkdown(text || '');
    displayMarkdown = insertAnnotations(sourceMarkdown);
  }

  displayMarkdown = transformPlainToCEX(displayMarkdown, billState) || noBillVersion;

  return (
    <div className="row" id="billView">
      <div className="col-12">
        <div className="bill-text" id="billText">
          <Markdown
            className="bill-text-markdown-parent"
            options={{
              overrides: {
                BillAnnotation: {
                  component: BillAnnotation,
                },
              },
            }}
          >
            {displayMarkdown}
          </Markdown>
        </div>

        {debug && (
          <p>
            <button onClick={() => setViewSourceMarkdown(true)} type="button">
              DEV: View source markdown
            </button>
          </p>
        )}
        {viewSourceMarkdown && (
          <div className="source-markdown">
            <h3>markdown BEFORE CEX transformation</h3>
            <pre>{sourceMarkdown}</pre>

            {baseText && (
              <div>
                <h3>base</h3>
                <pre>{baseText}</pre>
              </div>
            )}

            <h3>markdown AFTER CEX transformation</h3>
            <pre>{displayMarkdown}</pre>
          </div>
        )}
      </div>
      <BillAnnotationFromSelection
        billId={billId}
        billState={billState}
        billVersionId={billVersionId}
        diffTags={DIFF_TAGS}
        sourceBillText={sourceMarkdown}
      />
    </div>
  );
};

export default BillTextMarkdown;
